import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { ObjectDefinitionModel } from '@base/modules/rest/bbs-object/model/object-definition.model';
import { ModeEnum } from '@modules/master-data/model/mode.enum';
import { PrintTemplateRestService } from '@base/modules/rest/print-template/print-template-rest.service';
import {
  ItemsTableComponent,
  ItemsTableHeightTypeEnum
} from '@modules/master-data/components/items-table/items-table.component';
import { UserContext } from '@base/services/user-context';
import { ItemActionParamsModel } from '@views/secured/object/components/object/model/item-action-params.model';
import { ItemActionEnum } from '@views/secured/object/components/object/enums/item-action.enum';
import { ItemEditingCellStartModel } from '@views/secured/object/components/object/model/item-editing-cell-start.model';
import { concat, forkJoin, Observable, throwError } from 'rxjs';
import { downloadFile, saveStringAsFile } from '@shared/util/file.util';
import { catchError, map } from 'rxjs/operators';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { SnackBarService } from '@base/services/snackbar.service';
import { PrintTemplateModel } from '@base/modules/rest/print-template/model/print-template.model';
import { ObjectRestServiceCreator } from '@base/modules/rest/object/object-rest-service-creator';
import { JsonReportDataModel } from '@base/modules/rest/print-template/model/json-report-data.model';
import {
  DialogTemplateButtonComboStyleEnum
} from '@shared/components/templates/dialog-template/enums/dialog-template-button.enum';
import { UntypedFormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ObjectNameEnum } from '@base/modules/rest/master-data-history/model/enums/object-name.enum';
import { ExportTypeEnum } from '@shared/components/dialogs/export-dialog/enums/export-type.enum';
import { ObjectPrintRequestModel } from '@base/modules/rest/print-template/model/object-print-request.model';
import { PrintTemplateTypeEnum } from '../settings/print-templates/enums/print-template-type-enum';
import {
  ItemsTableSelectionChangeModel
} from '@modules/master-data/components/items-table/model/items-table-selection-change.model';

export enum PrintTypeEnum {
  DATA = 'DATA',
  ITEMS = 'ITEMS'
}

export interface PrintDialogResult {
  blobs: Blob[];
  templateName: string;
  templateId: number;
}

interface DialogData {
  objectName: string;
  type: PrintTypeEnum;
  params: { [key: string]: any };
  printData: any;
  fileName?: string | string[];
  skipAutoDownload?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-object-print-templates-dialog',
  templateUrl: './object-print-templates-dialog.component.html',
  styleUrls: ['./object-print-templates-dialog.component.scss'],
})
export class ObjectPrintTemplatesDialogComponent implements OnInit {
  @ViewChild(ItemsTableComponent) itemsTable: ItemsTableComponent;

  objectDefinition: ObjectDefinitionModel;
  isCyrillic = false;
  customFieldsLevelControl: UntypedFormControl;
  items: PrintTemplateModel[] = [];
  selectedTemplate: PrintTemplateModel;
  templatesMergedIntoPDF = false;
  DialogTemplateButtonComboStyleEnum = DialogTemplateButtonComboStyleEnum;
  ExportTypeEnum = ExportTypeEnum;
  exportType = ExportTypeEnum.PDF;

  PrintTemplateTypeEnum = PrintTemplateTypeEnum;
  ModeEnum = ModeEnum;
  ItemsTableHeightTypeEnum = ItemsTableHeightTypeEnum;
  showProgressSpinner: boolean;
  progressBarValue: number;
  toDownload: Blob[] = [];

  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogData,
              private dialogRef: MatDialogRef<ObjectPrintTemplatesDialogComponent, Blob | PrintDialogResult>,
              private printTemplateRestService: PrintTemplateRestService,
              private objectRestServiceCreator: ObjectRestServiceCreator,
              private snackBarService: SnackBarService,
              private userContext: UserContext,
              private translateService: TranslateService) {
  }

  ngOnInit(): void {
    this.customFieldsLevelControl = new UntypedFormControl(2, Validators.required);
    this.objectRestServiceCreator.create(ObjectNameEnum.PrintTemplatesDialog)
      .getDefinition()
      .subscribe(def => {
        this.objectDefinition = def;
      });

    const recordId = this.data.printData?.id;
    this.printTemplateRestService.getAllTemplates({objectName: this.data.objectName, recordId})
      .subscribe(res => {
        this.items = res;
        if (this.data.params?.automaticPrint) {
          this.selectedTemplate = this.items.shift();
          this.printDocument();
        }
      });
  }

  static open(dialog: MatDialog, data: DialogData): MatDialogRef<ObjectPrintTemplatesDialogComponent, Blob | PrintDialogResult> {
    return dialog.open(ObjectPrintTemplatesDialogComponent, {
      data,
      width: '1000px',
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  onItemsAction(event: ItemActionParamsModel): void {
    if (event.action === ItemActionEnum.EditingCellEnd) {
      this.selectedTemplate = event.data.item;
      const data: ItemEditingCellStartModel = event.data;
      const previousValue = data.item.selected;
      this.items
        .forEach(item => item.selected = false);
      data.item.selected = previousValue;
    }
    if (event.action === ItemActionEnum.SelectionChanged) {
      this.onSelectionChange(event);
    }
  }

  onSelectionChange(event: ItemActionParamsModel): void {
    this.selectedTemplate = (event.data as ItemsTableSelectionChangeModel)?.selectedRows?.length ? (event.data as ItemsTableSelectionChangeModel)?.selectedRows[0] : null;
  }

  onPrint(): void {
    if (!this.selectedTemplate) {
      return;
    }
    this.printDocument();
  }

  private printDocument(): void {
    let completedRequests = 0;
    this.showProgressSpinner = true;
    this.progressBarValue = 0;
    const printData: any[] = this.data.type === PrintTypeEnum.DATA ? [this.data.printData] : this.data.printData;
    const totalDocuments = printData?.length;

    const apis$: Observable<{ response: HttpResponse<Blob>; index: number }>[] = printData
      .map((item, index) => {
        const printFileName = Array.isArray(this.data.fileName) ? this.data.fileName[index] : this.data.fileName;

        return this.printTemplateRestService.printDocument(this.getPrintDataRequest(item, printFileName))
          .pipe(
            map(response => ({response, index})),
            catchError((errorResponse: HttpErrorResponse) => {
              this.showPrintError(errorResponse);
              this.showProgressSpinner = false;
              return throwError(() => errorResponse);
            })
          );
      });

    concat(...apis$)
      .subscribe({
        next: ({response}) => {
          completedRequests++;
          this.progressBarValue = Math.round((completedRequests / totalDocuments) * 100);

          const blob: Blob = response.body;
          const fileName: string = this.prepareFileName(response);
          if (this.data.skipAutoDownload) {
            this.toDownload.push(blob);
          } else {
            downloadFile(blob, fileName);
          }

          if (this.exportType !== ExportTypeEnum.PDF) {
            this.snackBarService.openSuccessMessage(`${this.translateService.instant('PRINT_TEMPLATE.SUCCESSFULLY_EXPORTED')} ${this.exportType}`);
            this.showProgressSpinner = false;
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.showPrintError(errorResponse);
          this.showProgressSpinner = false;
        },
        complete: () => {
          this.showProgressSpinner = false;
          if (!this.data.skipAutoDownload) {
            this.dialogRef.close();
          } else {
            this.dialogRef.close({
              blobs: this.toDownload,
              templateName: this.selectedTemplate?.name,
              templateId: this.selectedTemplate?.templateId
            } as PrintDialogResult);
          }
        },
      });
  }

  private showPrintError(errorResponse: HttpErrorResponse): void {
    const fallbackMessage =
      (errorResponse?.error as any)?.message ||
      errorResponse?.message ||
      this.translateService.instant('COMMON.ERROR');

    // For `responseType: 'blob'`, Angular returns backend JSON errors as a Blob.
    const errorBlob = errorResponse?.error;
    if (errorBlob instanceof Blob) {
      errorBlob
        .text()
        .then((text) => {
          if (!text) {
            this.snackBarService.openErrorMessage(fallbackMessage);
            return;
          }
          try {
            const parsed = JSON.parse(text);
            // Pass a HttpErrorResponse-like object so SnackBarService can show stackTrace/actions.
            const enriched = new HttpErrorResponse({
              error: parsed,
              status: errorResponse?.status,
              statusText: errorResponse?.statusText,
              url: errorResponse?.url || undefined,
            });
            this.snackBarService.openErrorMessage(enriched, false);
          } catch {
            // Non-JSON error (plain text)
            this.snackBarService.openErrorMessage(text || fallbackMessage);
          }
        })
        .catch(() => this.snackBarService.openErrorMessage(fallbackMessage));
      return;
    }

    // Non-blob errors: pass through so stackTrace can be displayed when present.
    this.snackBarService.openErrorMessage(errorResponse, false);
  }

  private prepareFileName(response: HttpResponse<Blob>): string {
    let fileName = 'document';
    const contentDisposition: string = response.headers.get('Content-Disposition');
    if (contentDisposition) {
      const matches = /filename="?(.+?)"?(\s*;|$)/.exec(contentDisposition);
      if (matches && matches[1]) {
        fileName = matches[1];
      }
    }
    return fileName;
  }

  onChangeTemplatesMerged(event: any): void {
    this.templatesMergedIntoPDF = event;
  }

  onChangeIsCyrillic(event: any): void {
    this.isCyrillic = event;
  }

  downloadJsonDataSource(): void {
    const printData: any[] = this.data.type === PrintTypeEnum.DATA ? [this.data.printData] : this.data.printData;
    const apis$: Observable<JsonReportDataModel>[] = printData
      .map(item =>
        this.printTemplateRestService.getPrintJson(this.getPrintDataRequest(item, this.data.objectName))
      );
    forkJoin(apis$)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          this.snackBarService.openErrorMessage(errorResponse);
          return throwError(() => errorResponse);
        })
      )
      .subscribe((response) => {
        response
          .forEach((item) => {
            const data = JSON.stringify({
              params: item.params,
              ...item.data,
            }, null, 4);
            saveStringAsFile(data, 'application/json', `${this.data.objectName}`);
          });
      });
  }

  private getPrintDataRequest(item: any, fileName: string): { objectName: string; data: ObjectPrintRequestModel } {
    return {
      objectName: this.data.objectName,
      data: {
        id: item?.id,
        printType: this.selectedTemplate?.printType,
        templateId: this.selectedTemplate?.templateId,
        params: this.data.params,
        data: item,
        idSPJ: this.userContext.spj?.id,
        isCyrillic: this.isCyrillic,
        customFieldsLevel: this.customFieldsLevelControl.value,
        selectedExportPrintType: this.exportType,
        fileName: fileName,
      },
    };
  }

  get isPdf(): boolean {
    return this.selectedTemplate?.printTemplateExportTypes?.includes(ExportTypeEnum.PDF);
  }

  get isDocx(): boolean {
    return this.selectedTemplate?.printTemplateExportTypes?.includes(ExportTypeEnum.DOCX);
  }

  get isXlsx(): boolean {
    return this.selectedTemplate?.printTemplateExportTypes?.includes(ExportTypeEnum.XLSX);
  }

}
